import { ChangeEvent, FormEvent, useState } from 'react';
import Layout from '../components/Layout';
import HeroSection from '../components/HeroSection';
import ProductSlider from '../components/ProductSlider';

type FormStatus = 'idle' | 'pending' | 'success' | 'error';

type WhyPascalineCard = {
  title: string;
  content: string;
  buttonText: string;
  buttonLink: string;
  image: string;
};

const whyPascalineCards: WhyPascalineCard[] = [
  {
    title: 'Offline First Approach',
    content:
      'Experience distraction-free learning at zero internet cost. Pascaline is engineered to function seamlessly without internet connectivity, ensuring uninterrupted access to high-quality educational resources in any environment. This approach eliminates digital barriers and empowers learners everywhere to thrive, regardless of connectivity constraints.',
    buttonText: 'Learn More',
    buttonLink: '/curriculum',
    image: '/images/card1.webp'
  },
  {
    title: 'Hands-On Learning with Interactive Tools',
    content:
      'Pascaline employs active, hands-on learning strategies and interactive digital tools to foster deep analytical reasoning and critical thinking in students. Our platform encourages exploration, experimentation, and engagement, making learning both effective and enjoyable.',
    buttonText: 'Explore Content',
    buttonLink: '/content',
    image: '/images/card2.webp',
  },
  {
    title: 'Combined Curriculum for Modern Education',
    content:
      'Pascaline integrates multiple curricula, including that of the NERDC, to deliver a superior and contemporary learning experience. Our unified curriculum is designed to boost both knowledge and practical skills, preparing students for the demands of the modern world.',
    buttonText: 'See Projects',
    buttonLink: '/projects',
    image: '/images/card3.webp'
  },
  {
    title: 'AI-Enhanced Assessment and Feedback',
    content:
      'Harnessing the power of artificial intelligence, Pascaline simplifies the marking process and provides timely, personalized feedback to learners. Our AI-driven assessment tools ensure accuracy, efficiency, and actionable insights, supporting continuous improvement and academic excellence.',
    buttonText: 'Features',
    buttonLink: '/features',
    image: '/images/card4.webp'
  }
];

const Home = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<{ type: FormStatus; message: string }>({
    type: 'idle',
    message: '',
  });

  const setField = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    console.log('Contact form update', { field: name, value });
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus({ type: 'pending', message: 'Sending your message...' });
    console.log('Contact submit triggered', formData);

    try {
      const response = await fetch('/api/send_message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const contentType = response.headers.get('content-type') ?? '';
      let payload: Record<string, unknown> | null = null;
      if (contentType.includes('application/json')) {
        payload = await response.json();
      } else {
        const text = await response.text();
        throw new Error(text || 'Server returned a non-JSON response');
      }

      console.log('Contact form response', { status: response.status, payload });

      if (!response.ok) {
        const serverError = typeof payload?.error === 'string' ? payload.error : 'Unable to send message';
        throw new Error(serverError);
      }

      console.log('Contact message saved', payload);

      setStatus({ type: 'success', message: 'Thanks! Pascaline received your message.' });
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Failed to submit contact message', error);
      setStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'An unexpected error occurred',
      });
    }
  };

  return (
    <Layout>
      <HeroSection />
      <ProductSlider />
      <section className="why-pascaline" aria-label="Why Pascaline">
        <h2 className="why-pascaline__title">Why Pascaline</h2>
        <div className="why-pascaline__cards">
          {whyPascalineCards.map(({ title, content, image }) => (
            <article key={title} className="why-card">
              <div className="why-card__image">
                <img src={image} alt={title} />
              </div>
              <div className="why-card__content">
                <h3>{title}</h3>
                <p>{content}</p>
              </div>
            </article>
          ))}
        </div>
      </section>
      <section id="contact" className="contact" aria-label="Contact information">
        <div className="contact__details">
          <p className="contact__eyebrow">Stay in the loop</p>
          <h2>Contact us</h2>
          <p>
            Drop us a message and we will reply within one business day. You can
            also call us.
          </p>
          <div className="contact__info">
            <a href="mailto:hello@pascalinekids.org">hello@pascalinekids.org</a>
            <a href="tel:+1234567890">+234 9054-821-617</a>
          </div>

        </div>
        <form 
          className="contact__form" 
          aria-label="Contact form"
          onSubmit={handleSubmit}
        >
          <label className="sr-only" htmlFor="contact-name">
            Name
          </label>
          <input 
            id="contact-name" 
            name="name" 
            placeholder="Your name" 
            required
            value={formData.name}
            onChange={setField}
          />
          <label className="sr-only" htmlFor="contact-email">
            Email
          </label>
          <input 
            id="contact-email" 
            name="email" 
            type="email" 
            placeholder="Your email" 
            required
            value={formData.email}
            onChange={setField}
          />
          <label className="sr-only" htmlFor="contact-message">
            Message
          </label>
          <textarea
            id="contact-message"
            name="message"
            rows={4}
            placeholder="Tell us what you are curious about"
            required
            value={formData.message}
            onChange={setField}
          />
          {status.message && (
            <p
              role="status"
              aria-live="polite"
              className={`contact__status contact__status--${status.type}`}
            >
              {status.message}
            </p>
          )}
          <button 
            type="submit" 
            className="primary"
            disabled={status.type === 'pending'}
          >
            Send message
          </button>
        </form>
      </section>
    </Layout>
  );
};

export default Home;
